import {
  Box,
  Flex,
  Icon,
  Progress,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';
// import Menu from 'components/menu/MainMenu';
import * as React from 'react';
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { MdCancel, MdCheckCircle, MdOutlineError } from 'react-icons/md';

import constants from 'providers/Constants';

const columnHelper = createColumnHelper();

export default function EndpointLogsTable(props) {
  const { tableData, noResultsImage, title, index, searchQuery } = props;
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState([]);
  const [{ pageIndex, pageSize }, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const columns = [
    columnHelper.accessor('endpoint', {
      id: 'endpoint',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t("ENDPOINT")}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t("STATUS")}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Icon
            w="24px"
            h="24px"
            me="5px"
            color={
              info.getValue() === 'Success'
                ? 'green.500'
                : info.getValue() === 'Failed'
                ? 'red.500'
                : info.getValue() === 'Error'
                ? 'orange.500'
                : null
            }
            as={
              info.getValue() === 'Success'
                ? MdCheckCircle
                : info.getValue() === 'Failed'
                ? MdCancel
                : info.getValue() === 'Error'
                ? MdOutlineError
                : null
            }
          />
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {t(info.getValue())}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t("DATE")}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {new Date(info.getValue()).toLocaleString()}
        </Text>
      ),
    }),
    columnHelper.accessor('used_token_percentage', {
      id: 'used_token_percentage',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          {t("TOKEN USAGE")}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Progress
            variant="table"
            colorScheme="brandScheme"
            h="8px"
            w="108px"
            value={info.getValue()}
          />
        </Flex>
      ),
    }),
  ];
  const [data, setData] = React.useState([...tableData]);

  useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  // Filter data based on search query
  const filteredData = React.useMemo(() => {
    if (!searchQuery || searchQuery.trim() === "") {
      return data;
    }
    const query = searchQuery.toLowerCase().trim();
    return data.filter((log) => {
      const endpoint = log.endpoint?.toLowerCase() || "";
      const status = log.status?.toLowerCase() || "";
      const method = log.method?.toLowerCase() || "";
      
      return endpoint.includes(query) || status.includes(query) || method.includes(query);
    });
  }, [data, searchQuery]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      pagination: { pageIndex, pageSize },
    },
    onPaginationChange: setPagination,
    setPageSize: pageSize,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  useEffect(() => {
  }, [data, title]);

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflow="hidden"
    >
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          {title || 'Table Title'}
        </Text>
      </Flex>
      <Box overflowX="auto">
        {data.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py="50px">
            {noResultsImage && (
              <Box mb="20px">
                <img src={noResultsImage} alt="No results" />
              </Box>
            )}
            <Text color={textColor} fontSize="sm" fontWeight="500">
              {t("No results found! Check")} <NavLink style={{ color: 'green' }} to={constants.urls.documentation} >{t("API documentation")}</NavLink> {t("for more details")}
            </Text>
          </Flex>
        ) : (
        <>
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex
                          justifyContent="space-between"
                          align="center"
                          fontSize={{ sm: '10px', lg: '12px' }}
                          color="gray.400"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: '',
                            desc: '',
                          }[header.column.getIsSorted()] ?? null}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table
                .getRowModel()
                .rows.map((row) => {
                  return (
                    <Tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <Td
                            key={cell.id}
                            fontSize={{ sm: '14px' }}
                            minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                            borderColor="transparent"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
          <Flex justifyContent="space-between" alignItems="center" px="25px">
            <Button
              onClick={() => {
                table.previousPage();
                setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
              }}
              disabled={!table.getCanPreviousPage()}
            >
              {t("Previous")}
            </Button>
            <Text>
              {t("Page")} {table.getState().pagination.pageIndex + 1} {t("of")} {' '}
              {table.getPageCount()}
            </Text>
            <Button
              onClick={() => {
                table.nextPage();
                setTimeout(() => index(table.getState().pagination.pageIndex + 1), 0);
              }}
              disabled={!table.getCanNextPage()}
            >
              {t("Next")}
            </Button>
          </Flex>
        </>
        )}
      </Box>
    </Card>
  );
}
